import { useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";

import { createApiClient } from "../../../infrastructure/http/apiClient";
import { loadAuthenticatedBoot } from "../../appBoot";
import { logoutSession, refreshSession } from "../../auth";
import { toShellError } from "../domain/errors";
import type { AuthenticatedShellPageProps, ShellState } from "../domain/shellTypes";

export function useShellBoot({
  initialBoot,
  initialAccessToken = "",
  onStaffBoot,
  onLogoutReset
}: Pick<AuthenticatedShellPageProps, "initialBoot" | "initialAccessToken"> & {
  onStaffBoot: () => void;
  onLogoutReset: () => void;
}) {
  const [manualToken, setManualToken] = useState("");
  const [sessionToken, setSessionToken] = useState(initialAccessToken);
  const [state, setState] = useState<ShellState>(initialBoot ? { status: "success", boot: initialBoot } : { status: "idle" });
  const [logoutState, setLogoutState] = useState<"idle" | "loading">("idle");
  const sessionBootAttemptedRef = useRef(false);
  const apiClient = useMemo(() => createApiClient({
    getAuthToken: () => sessionToken || manualToken.trim() || undefined
  }), [manualToken, sessionToken]);
  const hasInitialBoot = initialBoot !== undefined;

  useEffect(() => {
    if (hasInitialBoot || sessionToken || sessionBootAttemptedRef.current) {
      return;
    }

    let ignore = false;
    sessionBootAttemptedRef.current = true;
    setState({ status: "loading" });

    const publicApiClient = createApiClient();
    refreshSession(publicApiClient)
      .then((session) => {
        if (ignore) {
          return undefined;
        }

        const accessToken = session.access_token;
        const authenticatedApiClient = createApiClient({ getAuthToken: () => accessToken });
        return loadAuthenticatedBoot(authenticatedApiClient).then((boot) => ({ accessToken, boot }));
      })
      .then((result) => {
        if (!ignore && result) {
          setSessionToken(result.accessToken);
          setState({ status: "success", boot: result.boot });
          if (result.boot.user.role === "staff") {
            onStaffBoot();
          }
        }
      })
      .catch(() => {
        if (!ignore) {
          setState({ status: "unauthenticated" });
        }
      });

    return () => {
      ignore = true;
      if (!sessionToken) {
        sessionBootAttemptedRef.current = false;
      }
    };
  }, [hasInitialBoot, onStaffBoot, sessionToken]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!manualToken.trim()) {
      setState({
        status: "error",
        title: "Sessao ausente",
        message: "Informe uma sessao valida para carregar o app autenticado.",
        retryable: false
      });
      return;
    }

    setState({ status: "loading" });
    setSessionToken(manualToken.trim());

    try {
      const boot = await loadAuthenticatedBoot(apiClient);
      setState({ status: "success", boot });
      if (boot.user.role === "staff") {
        onStaffBoot();
      }
    } catch (error) {
      setState(toShellError(error));
    }
  }

  async function handleLogout() {
    setLogoutState("loading");

    try {
      await logoutSession(apiClient);
      setManualToken("");
      setSessionToken("");
      setState({ status: "idle" });
      onLogoutReset();

      if (typeof window !== "undefined") {
        window.location.assign("/go/login");
      }
    } catch {
      setState({
        status: "error",
        title: "Nao foi possivel sair",
        message: "Tente novamente para revogar a sessao com seguranca.",
        retryable: true
      });
    } finally {
      setLogoutState("idle");
    }
  }

  return {
    apiClient,
    hasInitialBoot,
    logoutDisabled: logoutState === "loading",
    manualToken,
    sessionToken,
    setManualToken,
    state,
    handleLogout,
    handleSubmit
  };
}
